import { useState, useEffect } from "react";
import { Box, FormControl, styled, Autocomplete, TextField } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import Tradename from "../../../api/ChemInv/Tradename";
import { ChemIDSearchField } from "../../../components/ChemIDSearch";
import Substance from "../../../api/LIMS/Substance";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "25px"
}));

const tradenameValidationSchema = (invalidTradenameNames) => {
  return Yup.object().shape({
    id: Yup.number(),
    tradenameName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Trade Name is required'),      
    standardShelfLifeMonths: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? undefined : value,
      )
      .typeError("Standard Shelf Life must be a number")
      .required('Standard Shelf Life is required'),
    referenceSubstance: Yup.object()
      .required('Chem ID is required')
      .test(
        'is-valid-tradename-chemID-combination',
        'Trade Name and Chem ID combination already exists',
        function(value) {
          const { tradenameName, id } = this.parent;

          if (!tradenameName || !value) return true;
          
          let isValid = true;          
            const isDuplicated = invalidTradenameNames.some(
              (t) =>
                t.tradenameName.toUpperCase() === tradenameName.toUpperCase() &&
                t.chemID === value.chemID &&
                t.id !== id
            );
            isValid = !isDuplicated;          
          return isValid;
        }
      )      
  });
}

const initialState = {
  id: 0,
  tradenameName: "",
  chemID: "",  
  referenceSubstance: null,
  createdByEmail: "",
  createdDate: "",
  lastModifiedByEmail: "",
  lastModifiedDate: "",
  standardShelfLifeMonths: "",  
};

const TradeNameManagement = () => {
  const [isNewTradename, setIsNewTradename] = useState(false);
  const [tradenameData, setTradenameData] = useState(initialState);
  const [tradenameList, setTradenameList] = useState([]);    
  const [isSaving, setIsSaving] = useState(false);  

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.STLCSME, Roles.Developer]);

  const initialValues = tradenameData;
  const invalidTradenameNames = tradenameList;

  const resetData = () => setTradenameData(initialState);  

  useEffect(() => {
    loadAllTradeNames();    
  }, []);

function loadAllTradeNames() {
  Tradename.getAll().then((res) => {
    const sorted = (res ?? []).slice().sort((a, b) =>
      (a.tradenameName ?? "").localeCompare(b.tradenameName ?? ""),
    );
    setTradenameList(sorted);
  });
}

  function handleTradenameSelection(tradename) {
    resetData();
    
    if (tradename?.tradenameName) {
      setIsNewTradename(false);      
      Substance.getByChemID(tradename.chemID, true, false).then((res) => {
        tradename.referenceSubstance = res.result;
        setTradenameData(tradename);
      });
    }
  }

  function handleAddNewTradeName() {
    setIsNewTradename(true);
    resetData();
  }

  const handleSubmit = async(values) => {
    setIsSaving(true);

    const updatedTradenameData = new Tradename({
      id: values.id,
      tradenameName: values.tradenameName,
      chemID: values.referenceSubstance.chemID,
      standardShelfLifeMonths: values.standardShelfLifeMonths,
    });

    try {
      const apiAction = isNewTradename
        ? Tradename.create(updatedTradenameData)
        : Tradename.update(updatedTradenameData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'Trade Name saved successfully!', 'Ok');
      setIsNewTradename(false);
      loadAllTradeNames();

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving Trade Name : ${error?.message}`, 'Ok');      
    } finally {
      setIsSaving(false);
    } 
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">
      <StyledBox>
        <FormControl sx={{ m: 1, width: "44rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading Trade Names..."}
              id="tradename-select"
              value={tradenameData}
              onChange={(e, value) => { handleTradenameSelection(value) }}
              disablePortal
              options={tradenameList ?? []}
              getOptionLabel={(option) => option?.tradenameName ?? ""}
              getOptionKey={(option) => option?.id ?? ""}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Trade Name"/> }
              autoSelect
            />
            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleAddNewTradeName}
              >Add New</GlobalSecondaryButton>
            }
          </StyledBox>
        </FormControl>
      </StyledBox>

      {(tradenameData.tradenameName || isNewTradename) && <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={() => tradenameValidationSchema(invalidTradenameNames)}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched, isValid, dirty }) => (
            <Form style={{margin:"10px", width: "40rem"}}>

              <StyledBox>
                <TextField
                  style={{width: "100%"}}
                  name="tradenameName"
                  label="Trade Name"
                  variant="outlined" size="small" margin="none" 
                  value={values.tradenameName}
                  onChange={handleChange}
                  onBlur={handleBlur}                  
                  error={touched.tradenameName && Boolean(errors.tradenameName)}
                  helperText={touched.tradenameName && errors.tradenameName}
                />    
              </StyledBox>
    
              <StyledBox>                
                <ChemIDSearchField
                    fontSize={16}
                    fieldWidth={"100%"}
                    selectedChemIDObject={values.referenceSubstance}
                    setSelectedChemIDObject={(e) => {setFieldValue("referenceSubstance", e); setFieldTouched("referenceSubstance", true, false);}}
                    hasErrors={touched.referenceSubstance && Boolean(errors.referenceSubstance)}                                     
                    setHasErrors={(e) => setFieldTouched('referenceSubstance', true, true)}
                    helperText={touched.referenceSubstance && errors.referenceSubstance}
                    allowInactiveChemIDs={false}
                    returnSubstanceStrucureData={true}
                  />
              </StyledBox>
              <StyledBox>
                <TextField                  
                  name="standardShelfLifeMonths"
                  style={{width: "50%"}}
                  label="Standard Shelf Life (Months)"
                  variant="outlined" size="small" margin="none" 
                  value={values.standardShelfLifeMonths}
                  onChange={(e)=> {
                      const value = e.target.value;
                      if(/^\d*$/.test(value)) { 
                          if (value === '' || (parseInt(value, 10) > 0 && !value.startsWith('0'))) {
                            setFieldValue("standardShelfLifeMonths", value);
                        }
                      }                      
                    }
                  }
                  onBlur={handleBlur}                  
                  error={touched.standardShelfLifeMonths && Boolean(errors.standardShelfLifeMonths)}
                  helperText={touched.standardShelfLifeMonths && errors.standardShelfLifeMonths}
                  type="number"
                />
              </StyledBox>
              <AuditInfoLabel auditObject={values} />            

              { canEdit &&
                <StyledBox style={{ marginTop: "30px", marginBottom: "20px" }}>
                  <GlobalButton sx={{ width: "8rem" }}
                    variant='contained'             
                    type="submit"
                    disabled={!dirty || isSaving }
                  >
                    Save
                  </GlobalButton>
                </StyledBox>
              }    
            </Form>
          )}
        </Formik>
      } 

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>
    </StyledBox>
  );
};

export default TradeNameManagement;
